/* ============================================================
   v2 filter bar — a faithful recreation of the prototype's
   command-console toolbar (date-range preset chips, mono-styled
   selects/inputs, RESET). Wired to the same context filter state
   and facet hooks the classic filter bar uses, so behavior
   (mutual exclusion, debounced text commit, facet reconciliation)
   is identical — only the chrome differs.

   Selects use MUI `Select` (not native `<select>`) so the open
   dropdown menu is themed dark to match the rest of the page,
   rather than the browser's native (light) option list.
   ============================================================ */
import {
  Box,
  MenuItem,
  Select,
  styled,
  Tooltip,
  type SelectChangeEvent,
} from "@mui/material"
import type React from "react"
import { useEffect, useMemo, useState } from "react"
import { dayjsUTC, isRelative, isSameLookback } from "../../../utils/dates"
import {
  PHCDateRangePicker,
  RELATIVE_DATERANGE_CHOICES,
  RelativeDateRangeChoiceId,
} from "../../Shared/PHCDateRangePicker"
import {
  GDELT_DATE_RANGE_TOOLTIP,
  GDELT_US_STATE_TOOLTIP,
  isNonUsCountrySelected,
} from "../constants"

const NWSS_DATE_RANGE_TOOLTIP =
  "Shows events with a detection date in range. CDC wastewater data is typically 1–2 weeks behind."
const MEMBER_TITLE_TOOLTIP =
  "Search any cluster member article title within matching clusters"
const CLUSTER_SUMMARY_TOOLTIP =
  "Search the LLM-generated cluster member title summary"
import { usePrecomputedBiothreatSignals } from "../PrecomputedBiothreatSignalsContext"
import { ALL_DISEASES, ALL_GEO } from "../types"
import {
  useGdeltFilterFacets,
  useNwssFilterFacets,
} from "../usePrecomputedFilterFacets"
import {
  V2_ACCENT,
  V2_FONT_MONO,
  V2_ON_ACCENT,
  V2_SURFACE,
  V2_TEXT,
} from "./biothreatV2Tokens"

const GDELT_PRESETS = [
  RelativeDateRangeChoiceId.TwentyFourHours,
  RelativeDateRangeChoiceId.ThreeDays,
  RelativeDateRangeChoiceId.SevenDays,
]
const NWSS_PRESETS = [
  RelativeDateRangeChoiceId.TwoWeeks,
  RelativeDateRangeChoiceId.ThreeWeeks,
  RelativeDateRangeChoiceId.OneMonth,
]

const Field = styled("div")({
  display: "flex",
  flexDirection: "column",
  gap: 8,
})

const Label = styled("span")({
  fontFamily: V2_FONT_MONO,
  fontSize: 12,
  letterSpacing: "0.1em",
  color: V2_TEXT.muted,
  fontWeight: 700,
  textTransform: "uppercase",
  display: "flex",
  alignItems: "center",
  gap: 4,
  marginBottom: 1,
})

const controlBase = {
  height: 38,
  background: "rgba(0,0,0,0.28)",
  border: `1px solid ${V2_SURFACE.hairlineStrong}`,
  borderRadius: 7,
  color: "#fff",
  fontFamily: V2_FONT_MONO,
  fontSize: 14,
  fontWeight: 600,
  padding: "0 12px",
  outline: "none",
  transition: "border-color .14s",
  minWidth: 0,
  boxSizing: "border-box" as const,
  "&:focus": { borderColor: V2_ACCENT.cyan },
}

const Input = styled("input")({
  ...controlBase,
  "&::placeholder": { color: V2_TEXT.disabled },
})

/**
 * Restyle the shared MUI range picker (start/end inputs + separator) to match
 * the console's dark controls, since it ships with the app's light-surface
 * chrome. The popover calendar inherits the app's dark MUI theme.
 */
const V2DateRangeContainer = styled("div")({
  display: "flex",
  "& .MuiInputBase-root": {
    height: 38,
    backgroundColor: "rgba(0,0,0,0.28)",
    borderRadius: 7,
    fontFamily: V2_FONT_MONO,
    fontSize: 13,
    fontWeight: 600,
    color: "#fff",
  },
  "& .MuiOutlinedInput-notchedOutline": {
    borderColor: V2_SURFACE.hairlineStrong,
  },
  "& .MuiInputBase-root:hover .MuiOutlinedInput-notchedOutline": {
    borderColor: V2_ACCENT.cyan,
  },
  "& .MuiInputBase-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
    borderColor: V2_ACCENT.cyan,
  },
  "& .MuiInputLabel-root": {
    fontFamily: V2_FONT_MONO,
    fontSize: 12,
    color: V2_TEXT.muted,
  },
  "& .MuiInputLabel-root.Mui-focused": { color: V2_ACCENT.cyan },
  "& input": { color: "#fff" },
  "& input::placeholder": { color: V2_TEXT.disabled, opacity: 1 },
  // The "to" separator rendered between the two inputs.
  "& .MuiTypography-root": { color: V2_TEXT.muted },
})

/** Closed-control styling for the MUI Select, matching the command inputs. */
const filterSelectSx = {
  height: 38,
  width: "100%",
  fontFamily: V2_FONT_MONO,
  fontSize: 14,
  fontWeight: 600,
  color: "#fff",
  backgroundColor: "rgba(0,0,0,0.28)",
  borderRadius: "7px",
  "& .MuiOutlinedInput-notchedOutline": {
    border: `1px solid ${V2_SURFACE.hairlineStrong}`,
  },
  "&:hover .MuiOutlinedInput-notchedOutline": {
    border: `1px solid ${V2_ACCENT.cyan}`,
  },
  "&.Mui-focused": {
    backgroundColor: "rgba(0,0,0,0.28)",
    "& .MuiOutlinedInput-notchedOutline": {
      border: `1px solid ${V2_ACCENT.cyan}`,
      boxShadow: "none",
    },
  },
  "& .MuiSelect-select": {
    padding: "0 12px",
    display: "flex",
    alignItems: "center",
    minHeight: "0 !important",
  },
  "&.Mui-disabled": { opacity: 0.4 },
} as const

/** Themed dark dropdown menu so options match the page, not the browser. */
const filterMenuProps = {
  slotProps: {
    paper: {
      sx: {
        mt: 0.5,
        maxHeight: 360,
        backgroundColor: V2_SURFACE.panelSolid,
        backgroundImage: "none",
        backdropFilter: "none",
        border: `1px solid ${V2_SURFACE.hairlineStrong}`,
        "& .MuiMenuItem-root": {
          fontFamily: V2_FONT_MONO,
          fontSize: 13,
          color: V2_TEXT.body,
          "&:hover": { backgroundColor: "rgba(255,255,255,0.06)" },
          "&.Mui-selected": {
            backgroundColor: V2_ACCENT.cyanSoft,
            color: "#fff",
          },
          "&.Mui-selected:hover": { backgroundColor: V2_ACCENT.cyanSoft },
        },
      },
    },
  },
}

const FilterSelect: React.FC<{
  value: string
  disabled?: boolean
  onChange: (event: SelectChangeEvent) => void
  children: React.ReactNode
}> = ({ value, disabled, onChange, children }) => (
  <Select
    value={value}
    disabled={disabled}
    onChange={onChange}
    size="small"
    sx={filterSelectSx}
    MenuProps={filterMenuProps}
  >
    {children}
  </Select>
)

const DChip = styled("button", {
  shouldForwardProp: prop => prop !== "active",
})<{ active?: boolean }>(({ active }) => ({
  height: 38,
  display: "inline-flex",
  alignItems: "center",
  boxSizing: "border-box",
  fontFamily: V2_FONT_MONO,
  fontSize: 13,
  fontWeight: 700,
  letterSpacing: "0.04em",
  padding: "0 15px",
  borderRadius: 8,
  border: `1px solid ${active ? V2_ACCENT.cyan : V2_SURFACE.hairlineStrong}`,
  background: active ? V2_ACCENT.cyan : "transparent",
  color: active ? V2_ON_ACCENT : "#AFB8C1",
  cursor: "pointer",
  transition: "all .12s",
  "&:hover": {
    borderColor: V2_ACCENT.cyan,
    color: active ? V2_ON_ACCENT : "#fff",
  },
}))

const ResetButton = styled("button")({
  height: 38,
  cursor: "pointer",
  fontFamily: V2_FONT_MONO,
  fontSize: 13,
  fontWeight: 700,
  letterSpacing: "0.08em",
  padding: "0 16px",
  borderRadius: 7,
  border: `1px solid ${V2_ACCENT.cyan}`,
  background: V2_ACCENT.cyan,
  color: V2_ON_ACCENT,
  textTransform: "uppercase",
})

const FieldLabel: React.FC<{
  children: React.ReactNode
  tooltip?: string
}> = ({ children, tooltip }) => (
  <Label>
    {tooltip ? (
      <Tooltip title={tooltip} arrow placement="top">
        <Box
          component="span"
          sx={{
            color: V2_ACCENT.cyan,
            fontSize: 12,
            opacity: 0.7,
            cursor: "help",
          }}
        >
          ⓘ
        </Box>
      </Tooltip>
    ) : null}
    {children}
  </Label>
)

/**
 * Active-date-range control: quick relative presets on the first line, with the
 * absolute start/end range picker below on its own line. Picking a custom range
 * switches the feed off its relative lookback (so no preset chip stays lit);
 * clicking a preset switches back. The picker is date-only — GDELT normalizes an
 * absolute range to whole UTC days and NWSS uses calendar dates, so the time of
 * day is never sent. Clamped to "now" since future days have no signals.
 */
const DateRangeField: React.FC<{
  presetIds: RelativeDateRangeChoiceId[]
  tooltip: string
}> = ({ presetIds, tooltip }) => {
  const { dateRange, setDateRange } = usePrecomputedBiothreatSignals()
  const [initialNow] = useState(() => dayjsUTC().toISOString())
  const valueIsRelative = isRelative(dateRange)
  const choices = RELATIVE_DATERANGE_CHOICES.filter(choice =>
    presetIds.includes(choice.id)
  )
  return (
    <Field>
      <FieldLabel tooltip={tooltip}>Active Date Range</FieldLabel>
      <Box sx={{ display: "flex", gap: "6px" }}>
        {choices.map(choice => (
          <DChip
            key={choice.id}
            active={valueIsRelative && isSameLookback(choice, dateRange)}
            onClick={() => setDateRange(choice)}
          >
            {choice.label}
          </DChip>
        ))}
      </Box>
      <V2DateRangeContainer>
        <PHCDateRangePicker
          initialValue={dateRange}
          maxDate={dayjsUTC(initialNow)}
          showTime={false}
          onChange={setDateRange}
        />
      </V2DateRangeContainer>
    </Field>
  )
}

/** Text field that commits on blur / Enter (mirrors the classic behavior). */
const TextFilter: React.FC<{
  label: string
  width: number
  placeholder: string
  committedValue: string
  onCommit: (value: string) => void
  tooltip?: string
}> = ({ label, width, placeholder, committedValue, onCommit, tooltip }) => {
  const [draft, setDraft] = useState(committedValue)
  const [prev, setPrev] = useState(committedValue)
  if (committedValue !== prev) {
    setPrev(committedValue)
    setDraft(committedValue)
  }
  const submit = () => {
    if (draft !== committedValue) onCommit(draft)
  }
  return (
    <Field style={{ width }}>
      <FieldLabel tooltip={tooltip}>{label}</FieldLabel>
      <Input
        value={draft}
        placeholder={placeholder}
        onChange={e => setDraft(e.target.value)}
        onBlur={submit}
        onKeyDown={e => {
          if (e.key === "Enter") {
            e.preventDefault()
            submit()
          }
        }}
      />
    </Field>
  )
}

export const BiothreatV2GdeltFilters: React.FC = () => {
  const { gdeltFilters, setGdeltFilters, resetGdeltFilters } =
    usePrecomputedBiothreatSignals()
  const { data: facets, isLoading } = useGdeltFilterFacets()
  const loading = isLoading && !facets

  // Reconcile selected facets when the available options change.
  useEffect(() => {
    if (!facets) return
    const next = { ...gdeltFilters }
    let changed = false
    if (
      gdeltFilters.disease !== ALL_DISEASES &&
      !facets.diseases.includes(gdeltFilters.disease)
    ) {
      next.disease = ALL_DISEASES
      changed = true
    }
    if (
      gdeltFilters.state !== ALL_GEO &&
      (!facets.states.includes(gdeltFilters.state) ||
        isNonUsCountrySelected(gdeltFilters.country))
    ) {
      next.state = ALL_GEO
      changed = true
    }
    if (
      gdeltFilters.country !== ALL_GEO &&
      !facets.countries.includes(gdeltFilters.country)
    ) {
      next.country = ALL_GEO
      changed = true
    }
    if (changed) setGdeltFilters(next)
  }, [facets, gdeltFilters, setGdeltFilters])

  const usStateDisabled =
    loading || isNonUsCountrySelected(gdeltFilters.country)

  return (
    <>
      <DateRangeField
        presetIds={GDELT_PRESETS}
        tooltip={GDELT_DATE_RANGE_TOOLTIP}
      />
      <Field style={{ width: 170 }}>
        <FieldLabel>Disease</FieldLabel>
        <FilterSelect
          value={gdeltFilters.disease}
          disabled={loading}
          onChange={e =>
            setGdeltFilters({ ...gdeltFilters, disease: e.target.value })
          }
        >
          <MenuItem value={ALL_DISEASES}>{ALL_DISEASES}</MenuItem>
          {(facets?.diseases ?? []).map(option => (
            <MenuItem key={option} value={option}>
              {option}
            </MenuItem>
          ))}
        </FilterSelect>
      </Field>
      <Field style={{ width: 150 }}>
        <FieldLabel>Country</FieldLabel>
        <FilterSelect
          value={gdeltFilters.country}
          disabled={loading}
          onChange={e => {
            const country = e.target.value
            setGdeltFilters({
              ...gdeltFilters,
              country,
              // Keep the US state when drilling into the United States; only a
              // non-US country is mutually exclusive with a US state.
              state: isNonUsCountrySelected(country)
                ? ALL_GEO
                : gdeltFilters.state,
            })
          }}
        >
          <MenuItem value={ALL_GEO}>{ALL_GEO}</MenuItem>
          {(facets?.countries ?? []).map(option => (
            <MenuItem key={option} value={option}>
              {option}
            </MenuItem>
          ))}
        </FilterSelect>
      </Field>
      <Field style={{ width: 130 }}>
        <FieldLabel tooltip={GDELT_US_STATE_TOOLTIP}>US State</FieldLabel>
        <FilterSelect
          value={gdeltFilters.state}
          disabled={usStateDisabled}
          onChange={e => {
            // Selecting a US state keeps the country (the dropdown is only
            // enabled for the US or "All"), so US + state coexist.
            setGdeltFilters({ ...gdeltFilters, state: e.target.value })
          }}
        >
          <MenuItem value={ALL_GEO}>{ALL_GEO}</MenuItem>
          {(facets?.states ?? []).map(option => (
            <MenuItem key={option} value={option}>
              {option}
            </MenuItem>
          ))}
        </FilterSelect>
      </Field>
      <TextFilter
        label="Latest Member Title"
        width={190}
        placeholder="Search title…"
        tooltip={MEMBER_TITLE_TOOLTIP}
        committedValue={gdeltFilters.memberTitleSearch}
        onCommit={memberTitleSearch =>
          setGdeltFilters({ ...gdeltFilters, memberTitleSearch })
        }
      />
      <TextFilter
        label="Cluster Summary"
        width={180}
        placeholder="Search summaries…"
        tooltip={CLUSTER_SUMMARY_TOOLTIP}
        committedValue={gdeltFilters.summarySearch}
        onCommit={summarySearch =>
          setGdeltFilters({ ...gdeltFilters, summarySearch })
        }
      />
      <ResetButton onClick={resetGdeltFilters}>Reset</ResetButton>
    </>
  )
}

export const BiothreatV2NwssFilters: React.FC = () => {
  const { nwssFilters, setNwssFilters, resetNwssFilters } =
    usePrecomputedBiothreatSignals()
  const { data: facets, isLoading } = useNwssFilterFacets()
  const loading = isLoading && !facets

  useEffect(() => {
    if (!facets) return
    const next = { ...nwssFilters }
    let changed = false
    if (
      nwssFilters.disease !== ALL_DISEASES &&
      !facets.diseases.includes(nwssFilters.disease)
    ) {
      next.disease = ALL_DISEASES
      changed = true
    }
    if (
      nwssFilters.state !== ALL_GEO &&
      !facets.states.includes(nwssFilters.state)
    ) {
      next.state = ALL_GEO
      changed = true
    }
    if (changed) setNwssFilters(next)
  }, [facets, nwssFilters, setNwssFilters])

  const diseaseOptions = useMemo(() => facets?.diseases ?? [], [facets])
  const stateOptions = useMemo(() => facets?.states ?? [], [facets])

  return (
    <>
      <DateRangeField
        presetIds={NWSS_PRESETS}
        tooltip={NWSS_DATE_RANGE_TOOLTIP}
      />
      <Field style={{ width: 190 }}>
        <FieldLabel>Disease</FieldLabel>
        <FilterSelect
          value={nwssFilters.disease}
          disabled={loading}
          onChange={e =>
            setNwssFilters({ ...nwssFilters, disease: e.target.value })
          }
        >
          <MenuItem value={ALL_DISEASES}>{ALL_DISEASES}</MenuItem>
          {diseaseOptions.map(option => (
            <MenuItem key={option} value={option}>
              {option}
            </MenuItem>
          ))}
        </FilterSelect>
      </Field>
      <Field style={{ width: 130 }}>
        <FieldLabel>State</FieldLabel>
        <FilterSelect
          value={nwssFilters.state}
          disabled={loading}
          onChange={e =>
            setNwssFilters({ ...nwssFilters, state: e.target.value })
          }
        >
          <MenuItem value={ALL_GEO}>{ALL_GEO}</MenuItem>
          {stateOptions.map(option => (
            <MenuItem key={option} value={option}>
              {option}
            </MenuItem>
          ))}
        </FilterSelect>
      </Field>
      <ResetButton onClick={resetNwssFilters}>Reset</ResetButton>
    </>
  )
}
